import type { ComponentType } from 'react';
import { defineMessages, type IntlShape, type MessageDescriptor } from 'react-intl';

import type { QuickInsertPreview } from '@atlaskit/editor-common/quick-insert/preview';
import { messages as quickInsertMessages } from '@atlaskit/editor-common/quick-insert/messages';
import { ConfluenceAttributionIcon } from '@atlaskit/editor-common/quick-insert/confluence-attribution-icon';
import { JiraAttributionIcon } from '@atlaskit/editor-common/quick-insert/jira-attribution-icon';

import { EXTENSION_KEY, MACROS_WITH_LIVE_DOC_REFERENCE } from '../extensionConstants.ts';

const i18n = defineMessages({
	anchorMacroTitle: {
		id: 'fabric-extension-lib.anchor.title',
		description:
			'Title text to be shown for anchor macro in the macro browser, quick insert menu, and config panel',
		defaultMessage: 'Anchor link',
	},
	anchorMacroDescription: {
		id: 'fabric-extension-lib.anchor.description',
		description:
			'Description text to be shown for anchor macro in the macro browser, quick insert menu, and config panel',
		defaultMessage: 'Create a direct link to any section',
	},
	opsgenieAttribution: {
		id: 'fabric-extension-lib.quick-insert.preview-attribution.opsgenie',
		description: 'Opsgenie product name shown in a Quick Insert preview attribution.',
		defaultMessage: 'Opsgenie',
	},
	childrenMacroTitle: {
		id: 'fabric-extension-lib.children.live-docs.title',
		description:
			'New title for "Child pages" macro to account for both pages and live docs content types. "Child items" is the new macro name.',
		defaultMessage: 'Child items',
	},
	childrenMacroDescription: {
		id: 'fabric-extension-lib.children.live-docs.description',
		description:
			'Description for Child items macro to account for both pages and live docs content types.',
		defaultMessage: 'List content beneath pages or live docs',
	},
	detailsMacroTitle: {
		id: 'fabric-extension-lib.details.live-docs.title',
		description:
			'New title for Page properties macro to account for both pages and live docs content types. "Content properties" is the new macro name.',
		defaultMessage: 'Content properties',
	},
	detailsMacroDescription: {
		id: 'fabric-extension-lib.details.live-docs.description',
		description:
			'Description for Content properties macro to account for both pages and live docs content types.',
		defaultMessage: 'Capture summary information for content reports',
	},
	pageTreeMacroTitle: {
		id: 'fabric-extension-lib.pagetree.live-docs.title',
		description:
			'New title for Page tree macro to account for multiple content types. "Content tree" is the new macro name.',
		defaultMessage: 'Content tree',
	},
	pageTreeMacroDescription: {
		id: 'fabric-extension-lib.pagetree.live-docs.description',
		description: 'Description for Content tree macro to account for multiple content types.',
		defaultMessage: 'Display a full or partial content tree',
	},
	includeMacroTitle: {
		id: 'fabric-extension-lib.include.live-docs.title',
		description:
			'New title for Include Page macro to account for multiple content types. "Include content" is the new macro name.',
		defaultMessage: 'Include content',
	},
	includeMacroDescription: {
		id: 'fabric-extension-lib.include.live-docs.description',
		description:
			'Description for Include content macro to account for both page and live doc content types.',
		defaultMessage: 'Embed a full page or live doc',
	},
	detailsSummaryMacroTitle: {
		id: 'fabric-extension-lib.details-summary.live-docs.title',
		description:
			'New title for Page properties report macro to account for multiple content types. "Content properties report" is the new macro name.',
		defaultMessage: 'Content properties report',
	},
	detailsSummaryMacroDescription: {
		id: 'fabric-extension-lib.details-summary.live-docs.description',
		description:
			'Description for Content properties report macro to account for both page and live doc content types.',
		defaultMessage: 'Compile content summaries by label',
	},
	jiraMacroTitle: {
		id: 'fabric-extension-lib.jira.title',
		description:
			'Title text to be shown for legacy Jira macro in the macro browser, quick insert menu, and config panel',
		defaultMessage: 'Jira Data Center',
	},
	jiraMacroDescription: {
		id: 'fabric-extension-lib.jira.description',
		description:
			'Description text to be shown for Jira Data Center macro in the macro browser, quick insert menu, and config panel',
		defaultMessage: 'Display Data Center work items using JQL',
	},
});

export type QuickInsertPreviewOverride = Omit<QuickInsertPreview, 'attribution'> & {
	attribution?: { message: MessageDescriptor; icon?: ComponentType };
};

export const localizeQuickInsertPreview = (
	preview: QuickInsertPreviewOverride,
	intl: IntlShape,
): QuickInsertPreview => {
	const { attribution, ...previewWithoutAttribution } = preview;
	if (!attribution) {
		return previewWithoutAttribution;
	}
	return {
		...previewWithoutAttribution,
		attribution: {
			name: intl.formatMessage(attribution.message),
			...(attribution.icon ? { icon: attribution.icon } : {}),
		},
	};
};

const macroQuickInsertPreviewOverrides: Readonly<Record<string, QuickInsertPreviewOverride>> = {
	iframe: {
		attribution: {
			message: quickInsertMessages.previewAttributionConfluence,
			icon: ConfluenceAttributionIcon,
		},
		image: {
			dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVI8.png',
			light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVLM.png',
		},
	},
	'blog-posts': {
		attribution: {
			message: quickInsertMessages.previewAttributionConfluence,
			icon: ConfluenceAttributionIcon,
		},
		image: {
			dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVIU.png',
			light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVLJ.png',
		},
	},
	'change-history': {
		attribution: {
			message: quickInsertMessages.previewAttributionConfluence,
			icon: ConfluenceAttributionIcon,
		},
		image: {
			dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVIA.png',
			light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVMK.png',
		},
	},
	children: {
		attribution: {
			message: quickInsertMessages.previewAttributionConfluence,
			icon: ConfluenceAttributionIcon,
		},
		image: {
			dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVIP.png',
			light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVME.png',
		},
	},
	details: {
		attribution: {
			message: quickInsertMessages.previewAttributionConfluence,
			icon: ConfluenceAttributionIcon,
		},
		image: {
			dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVIQ.png',
			light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVLR.png',
		},
	},
	detailssummary: {
		attribution: {
			message: quickInsertMessages.previewAttributionConfluence,
			icon: ConfluenceAttributionIcon,
		},
		image: {
			dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVIS.png',
			light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVM9.png',
		},
	},
	'content-report-table': {
		attribution: {
			message: quickInsertMessages.previewAttributionConfluence,
			icon: ConfluenceAttributionIcon,
		},
		image: {
			dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVI6.png',
			light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVMC.png',
		},
	},
	pagetree: {
		attribution: {
			message: quickInsertMessages.previewAttributionConfluence,
			icon: ConfluenceAttributionIcon,
		},
		image: {
			dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVIV.png',
			light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVNC.png',
		},
	},
	contributors: {
		attribution: {
			message: quickInsertMessages.previewAttributionConfluence,
			icon: ConfluenceAttributionIcon,
		},
		image: {
			dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVJ0.png',
			light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVL1.png',
		},
	},
	decisionreport: {
		attribution: {
			message: quickInsertMessages.previewAttributionConfluence,
			icon: ConfluenceAttributionIcon,
		},
		image: {
			dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVHF.png',
			light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVMF.png',
		},
	},
	contentbylabel: {
		attribution: {
			message: quickInsertMessages.previewAttributionConfluence,
			icon: ConfluenceAttributionIcon,
		},
		image: {
			dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVIX.png',
			light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVKY.png',
		},
	},
	listlabels: {
		attribution: {
			message: quickInsertMessages.previewAttributionConfluence,
			icon: ConfluenceAttributionIcon,
		},
		image: {
			dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVHS.png',
			light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVMI.png',
		},
	},
	livesearch: {
		attribution: {
			message: quickInsertMessages.previewAttributionConfluence,
			icon: ConfluenceAttributionIcon,
		},
		image: {
			dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVIL.png',
			light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVL4.png',
		},
	},
	pagetreesearch: {
		attribution: {
			message: quickInsertMessages.previewAttributionConfluence,
			icon: ConfluenceAttributionIcon,
		},
		image: {
			dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVK0.png',
			light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVMB.png',
		},
	},
	'popular-labels': {
		attribution: {
			message: quickInsertMessages.previewAttributionConfluence,
			icon: ConfluenceAttributionIcon,
		},
		image: {
			dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVJ4.png',
			light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVKR.png',
		},
	},
	'questionslist-macro-native': {
		attribution: {
			message: quickInsertMessages.previewAttributionConfluence,
			icon: ConfluenceAttributionIcon,
		},
		image: {
			dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVHQ.png',
			light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVMH.png',
		},
	},
	'recently-updated': {
		attribution: {
			message: quickInsertMessages.previewAttributionConfluence,
			icon: ConfluenceAttributionIcon,
		},
		image: {
			dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVJ5.png',
			light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVKX.png',
		},
	},
	'tasks-report-macro': {
		attribution: {
			message: quickInsertMessages.previewAttributionConfluence,
			icon: ConfluenceAttributionIcon,
		},
		image: {
			dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVHJ.png',
			light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVL3.png',
		},
	},
	calendar: {
		attribution: {
			message: quickInsertMessages.previewAttributionConfluence,
			icon: ConfluenceAttributionIcon,
		},
		image: {
			dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVJ8.png',
			light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVM5.png',
		},
	},
	userlister: {
		attribution: {
			message: quickInsertMessages.previewAttributionConfluence,
			icon: ConfluenceAttributionIcon,
		},
		image: {
			dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVJ3.png',
			light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVLH.png',
		},
	},
	profile: {
		attribution: {
			message: quickInsertMessages.previewAttributionConfluence,
			icon: ConfluenceAttributionIcon,
		},
		image: {
			dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVI1.png',
			light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVML.png',
		},
	},
	excerpt: {
		attribution: {
			message: quickInsertMessages.previewAttributionConfluence,
			icon: ConfluenceAttributionIcon,
		},
		image: {
			dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVJT.png',
			light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVLX.png',
		},
	},
	'excerpt-include': {
		attribution: {
			message: quickInsertMessages.previewAttributionConfluence,
			icon: ConfluenceAttributionIcon,
		},
		image: {
			dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVJR.png',
			light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVN9.png',
		},
	},
	include: {
		attribution: {
			message: quickInsertMessages.previewAttributionConfluence,
			icon: ConfluenceAttributionIcon,
		},
		image: {
			dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVHV.png',
			light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVNP.png',
		},
	},
	jirachart: {
		attribution: { message: quickInsertMessages.previewAttributionJira, icon: JiraAttributionIcon },
		image: {
			dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12P300.png',
			light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12P2ZZ.png',
		},
	},
	jira: {
		attribution: { message: quickInsertMessages.previewAttributionJira, icon: JiraAttributionIcon },
		image: {
			dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVJW.png',
			light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVND.png',
		},
	},
	portfolioforjiraplan: {
		attribution: { message: quickInsertMessages.previewAttributionJira, icon: JiraAttributionIcon },
		image: {
			dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVJX.png',
			light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVNF.png',
		},
	},
	'opsgenie-incident-timeline-eu-macro': { attribution: { message: i18n.opsgenieAttribution } },
	'sharelinks-urlmacro': {
		attribution: {
			message: quickInsertMessages.previewAttributionConfluence,
			icon: ConfluenceAttributionIcon,
		},
		image: {
			dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVHZ.png',
			light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVL7.png',
		},
	},
	'toc-zone': {
		attribution: {
			message: quickInsertMessages.previewAttributionConfluence,
			icon: ConfluenceAttributionIcon,
		},
		image: {
			dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVJS.png',
			light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVNE.png',
		},
	},
	viewpdf: {
		attribution: {
			message: quickInsertMessages.previewAttributionConfluence,
			icon: ConfluenceAttributionIcon,
		},
		image: {
			dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12P2ZX.png',
			light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12P2ZY.png',
		},
	},
	viewxls: {
		attribution: {
			message: quickInsertMessages.previewAttributionMicrosoft,
		},
		image: {
			dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVIW.png',
			light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVL2.png',
		},
	},
	'profile-picture': {
		attribution: {
			message: quickInsertMessages.previewAttributionConfluence,
			icon: ConfluenceAttributionIcon,
		},
		image: {
			dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVIN.png',
			light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVLS.png',
		},
	},
	anchor: {
		attribution: {
			message: quickInsertMessages.previewAttributionConfluence,
			icon: ConfluenceAttributionIcon,
		},
		image: {
			dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVHH.png',
			light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVKZ.png',
		},
	},
	toc: {
		attribution: {
			message: quickInsertMessages.previewAttributionConfluence,
			icon: ConfluenceAttributionIcon,
		},
		image: {
			dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVJG.png',
			light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVMX.png',
		},
	},
	'create-from-template': {
		attribution: {
			message: quickInsertMessages.previewAttributionConfluence,
			icon: ConfluenceAttributionIcon,
		},
		image: {
			dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OZ55.png',
			light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OZ54.png',
		},
	},
	widget: {
		attribution: {
			message: quickInsertMessages.previewAttributionConfluence,
			icon: ConfluenceAttributionIcon,
		},
		image: {
			dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVK7.png',
			light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVL9.png',
		},
	},
	roadmap: {
		attribution: {
			message: quickInsertMessages.previewAttributionConfluence,
			icon: ConfluenceAttributionIcon,
		},
	},
	attachments: {
		attribution: {
			message: quickInsertMessages.previewAttributionConfluence,
			icon: ConfluenceAttributionIcon,
		},
	},
};

export const getMacroQuickInsertPreviewOverride = (
	extensionKey: string,
	intl: IntlShape,
): QuickInsertPreview | undefined => {
	const preview = macroQuickInsertPreviewOverrides[extensionKey];
	return preview ? localizeQuickInsertPreview(preview, intl) : undefined;
};

export const getMacroTitleOverride = (extensionKey: string, intl: IntlShape): string | null => {
	// Override new macro title for anchor macro
	if (extensionKey === EXTENSION_KEY.ANCHOR) {
		return intl.formatMessage(i18n.anchorMacroTitle);
	}

	if (extensionKey === EXTENSION_KEY.JIRA) {
		return intl.formatMessage(i18n.jiraMacroTitle);
	}

	if (MACROS_WITH_LIVE_DOC_REFERENCE.includes(extensionKey)) {
		switch (extensionKey) {
			case EXTENSION_KEY.CHILDREN:
				return intl.formatMessage(i18n.childrenMacroTitle);

			case EXTENSION_KEY.DETAILS:
				return intl.formatMessage(i18n.detailsMacroTitle);

			case EXTENSION_KEY.PAGE_TREE:
				return intl.formatMessage(i18n.pageTreeMacroTitle);

			case EXTENSION_KEY.INCLUDE:
				return intl.formatMessage(i18n.includeMacroTitle);

			case EXTENSION_KEY.DETAILS_SUMMARY:
				return intl.formatMessage(i18n.detailsSummaryMacroTitle);
		}
	}

	return null;
};

export const getMacroDescriptionOverride = (
	extensionKey: string,
	intl: IntlShape,
): string | null => {
	// Override new macro title and description for anchor macro
	if (extensionKey === EXTENSION_KEY.ANCHOR) {
		return intl.formatMessage(i18n.anchorMacroDescription);
	}

	if (extensionKey === EXTENSION_KEY.JIRA) {
		return intl.formatMessage(i18n.jiraMacroDescription);
	}

	// PGXT-7617 Update macro strings referencing page content types to also include live docs content types
	if (MACROS_WITH_LIVE_DOC_REFERENCE.includes(extensionKey)) {
		switch (extensionKey) {
			case EXTENSION_KEY.CHILDREN:
				return intl.formatMessage(i18n.childrenMacroDescription);

			case EXTENSION_KEY.DETAILS:
				return intl.formatMessage(i18n.detailsMacroDescription);

			case EXTENSION_KEY.PAGE_TREE:
				return intl.formatMessage(i18n.pageTreeMacroDescription);

			case EXTENSION_KEY.INCLUDE:
				return intl.formatMessage(i18n.includeMacroDescription);

			case EXTENSION_KEY.DETAILS_SUMMARY:
				return intl.formatMessage(i18n.detailsSummaryMacroDescription);
		}
	}

	return null;
};
